import {useMemo} from 'react'
import {isRetargetableLine} from '../helpers/line-helpers'
import type {ClientDiffLine} from '../types'
import {useAllSelectedLinesAreRetargetable} from '../stores/selectors'

interface UseAllLinesAreRetargetableParams {
  /**
   * The current diff line being interacted with
   */
  line?: ClientDiffLine

  /**
   * Whether the current row (and potentially other rows) are selected
   */
  isRowSelected: boolean
}

/**
 * Hook to determine if all lines in the current selection (or single line) are retargetable.
 * Retargetable lines are those that can be moved to the right side: context, injected context, or empty lines.
 *
 * This is useful for components that need to know whether to show retargeting UI or behavior
 * without needing the full conversation start information.
 *
 * @returns true if all selected lines are context, injected context, or empty; false otherwise
 */
export function useAllLinesAreRetargetable({line, isRowSelected}: UseAllLinesAreRetargetableParams): boolean {
  const allSelectedLinesAreRetargetable = useAllSelectedLinesAreRetargetable()

  return useMemo(() => {
    if (!isRowSelected) {
      // Single line case
      return !!line && isRetargetableLine(line)
    }

    // Use the selector for multi-line case
    return allSelectedLinesAreRetargetable()
  }, [allSelectedLinesAreRetargetable, isRowSelected, line])
}
